import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BIBLE_BOOKS } from '../../../../shared/types';
import { filterBooks } from '../../utils/bookUtils';
import { Time } from './types';
import AnimatedCheckmark from './AnimatedCheckmark';
import ChapterProgressGrid from '../../components/ChapterProgressGrid';
import { apiClient } from '../../hooks/useApi';

interface EndSessionDrawerProps {
  time: Time;
  isOpen: boolean;
  onClose: () => void;
  onSave: (minutes: number, book: string, chapters: number[], notes: string) => Promise<void>;
  defaultBook?: string;
  defaultChapter?: number;
  /** Current read-through number from the user's profile */
  currentReadThrough?: number;
}

const EndSessionDrawer = ({ time, isOpen, onClose, onSave, defaultBook, defaultChapter, currentReadThrough }: EndSessionDrawerProps) => {
  const [description, setDescription] = useState('');
  const [book, setBook] = useState(defaultBook ?? '');
  const [bookQuery, setBookQuery] = useState(defaultBook ?? '');
  const [showBookDropdown, setShowBookDropdown] = useState(false);
  const [selectedChapters, setSelectedChapters] = useState<Set<number>>(
    () => new Set(defaultChapter ? [defaultChapter] : [])
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [priorCompletedChapters, setPriorCompletedChapters] = useState<Set<number>>(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    if (hasSubmitted) {
      const id = setTimeout(() => navigate('/app'), 2500);
      return () => clearTimeout(id);
    }
  }, [hasSubmitted, navigate]);

  // Pre-fetch existing chapter progress for the book so we can merge it into the grid
  useEffect(() => {
    if (defaultBook && isOpen) {
      apiClient.getProgress(defaultBook, currentReadThrough).then((data: any) => {
        const nums = new Set<number>(
          (data?.completedChapters ?? []).map((c: { chapter: number }) => c.chapter)
        );
        setPriorCompletedChapters(nums);
      }).catch(() => {});
    }
  }, [defaultBook, isOpen, currentReadThrough]);

  if (!isOpen) return null;

  const filteredBooks = filterBooks(bookQuery, BIBLE_BOOKS);

  const toggleChapter = (ch: number) => {
    setSelectedChapters((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) {
        next.delete(ch);
      } else {
        next.add(ch);
      }
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-parchment-50 rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-lg relative flex flex-col max-h-[90dvh]">
        {/* Fixed header */}
        <div className="px-6 pt-6 pb-0 shrink-0">
          {!hasSubmitted && (
            <button
              onClick={() => { if (!isSubmitting) { setDescription(''); onClose(); } }}
              className="absolute top-4 right-4 text-ink-muted hover:text-ink text-2xl"
            >
              ×
            </button>
          )}
          <h2 className="text-xl font-bold text-ink mb-4">End Reading Session</h2>
        </div>

        {hasSubmitted ? (
          <div className="flex flex-col items-center py-6 gap-4 px-6 pb-6 overflow-y-auto">
            <AnimatedCheckmark />
            <p className="text-lg font-semibold text-ink">Session Saved!</p>
            {book && (
              <div className="w-full mt-2">
                <ChapterProgressGrid
                  book={book}
                  completedChapters={new Set([...priorCompletedChapters, ...selectedChapters])}
                  readThrough={currentReadThrough}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
          <div className="flex flex-col gap-4 text-center overflow-y-auto flex-1 min-h-0 px-6 py-2">
            <p className="text-ink-muted">You completed</p>
            <p className="text-xl font-bold text-leather">{time.totalSessionTime}</p>
            <p className="text-ink-muted mb-2">minutes this session</p>

            <div className="text-left flex flex-col gap-3">
              <label className="text-sm text-ink-muted block">What did you read?</label>

              {/* Book search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search book…"
                  value={bookQuery}
                  onChange={(e) => {
                    setBookQuery(e.target.value);
                    setBook('');
                    setShowBookDropdown(true);
                  }}
                  onFocus={() => setShowBookDropdown(true)}
                  onBlur={() => setTimeout(() => setShowBookDropdown(false), 150)}
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-leather bg-parchment-50"
                />
                {showBookDropdown && filteredBooks.length > 0 && (
                  <ul className="absolute z-50 w-full mt-1 bg-parchment-50 border border-parchment-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {filteredBooks.map((b) => (
                      <li key={b.name}>
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setBook(b.name);
                            setBookQuery(b.name);
                            setSelectedChapters(new Set());
                            setShowBookDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-parchment-200 hover:text-leather flex items-center justify-between"
                        >
                          <span>{b.name}</span>
                          <span className="text-xs text-ink-muted">{b.chapters} ch · {b.testament === 'old' ? 'OT' : 'NT'}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Interactive chapter grid */}
              {book && (
                <ChapterProgressGrid
                  book={book}
                  completedChapters={priorCompletedChapters}
                  selectedChapters={selectedChapters}
                  onToggle={toggleChapter}
                  readThrough={currentReadThrough}
                />
              )}
            </div>

            <div className="text-left">
              <label className="text-sm text-ink-muted mb-1 block">
                Notes (optional) — {description.length}/100
              </label>
              <textarea
                rows={3}
                placeholder="What did you read? Any reflections?"
                value={description}
                onChange={(e) => { if (e.target.value.length <= 100) setDescription(e.target.value); }}
                className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm text-ink resize-none focus:outline-none focus:ring-2 focus:ring-leather bg-parchment-50"
              />
            </div>

            {saveError && (
              <p className="text-sm text-red-600 text-center -mb-1">{saveError}</p>
            )}
          </div>

          {/* Pinned button row */}
          <div className="flex gap-3 px-6 py-4 border-t border-parchment-200 shrink-0">
              <button
                onClick={() => { setDescription(''); onClose(); }}
                className="flex-1 py-3 rounded-lg bg-parchment-200 hover:bg-parchment-300 text-ink font-semibold transition"
              >
                Cancel
              </button>
              <button
                disabled={isSubmitting}
                onClick={async () => {
                  setIsSubmitting(true);
                  setSaveError('');
                  try {
                    await onSave(time.totalSessionTime, book, Array.from(selectedChapters).sort((a, b) => a - b), description);
                    setHasSubmitted(true);
                  } catch (err: any) {
                    setSaveError(err?.message ?? 'Failed to save — please try again.');
                    setIsSubmitting(false);
                  }
                }}
                className="flex-1 py-3 rounded-lg bg-leather hover:bg-leather-light text-parchment-100 font-semibold transition disabled:opacity-50"
              >
                {isSubmitting ? 'Saving…' : 'Save Session'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EndSessionDrawer;
